import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BuildingOfficeIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, GlobeAltIcon, Squares2X2Icon, ListBulletIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyService } from '../services/companyService';
import { CompanyCard } from './CompanyCard';
import { useDebounce } from '@hooks/useDebounce';
import { useAuthStore } from '@features/auth/store/authStore';
import { getDefaultRouteForRole } from '@lib/utils/roleNavigation';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 12;

export function CompanySelectorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Solo super admin puede ver esta página
  // Si no es super admin, redirigir siempre
  const isSuperAdmin = user?.isSuperAdmin === true;

  useEffect(() => {
    if (user && !isSuperAdmin) {
      const defaultRoute = getDefaultRouteForRole(user?.role);
      navigate(defaultRoute, { replace: true });
    }
  }, [navigate, user, isSuperAdmin]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const debouncedSearch = useDebounce(searchText, 300);

  // Search companies with pagination — disabled if not super admin
  const {
    data: companiesData,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['companies', 'search', page, pageSize, debouncedSearch],
    queryFn: () => companyService.searchCompanies(page, pageSize, debouncedSearch || undefined),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (previousData) => previousData,
    enabled: isSuperAdmin,
  });

  // Select company mutation
  const selectCompanyMutation = useMutation({
    mutationFn: (companyId: string) => companyService.selectCompany(companyId),
    onSuccess: async () => {
      // Clear all company-specific cache to prevent stale data between companies
      queryClient.clear();
      localStorage.removeItem('invoice-filter-storage');

      // Full page reload to ensure all state (React Query cache, auth store, etc.)
      // is fresh for the new company. navigate() caused race conditions where
      // queries fired before Redis was fully updated with the new company.
      const defaultRoute = getDefaultRouteForRole(user?.role);
      window.location.href = defaultRoute;
    },
    onError: (error: Error) => {
      console.error('Failed to select company:', error);
    },
  });

  const companies = companiesData?.companies || [];
  const totalPages = companiesData?.totalPages || 1;
  const total = companiesData?.total || 0;

  // Fetch subscription statuses for visible companies
  const companiesForStatus = companies.filter(c => c.id && c.code).map(c => ({ id: c.id, code: c.code! }));
  const { data: subscriptionStatuses } = useQuery({
    queryKey: ['subscription-statuses', companiesForStatus.map(c => c.id).join(',')],
    queryFn: () => companyService.getCompaniesSubscriptionStatuses(companiesForStatus),
    enabled: companiesForStatus.length > 0,
    staleTime: 60 * 1000,
  });

  // Auto-select if only one company and no search active
  useEffect(() => {
    if (companies.length === 1 && !debouncedSearch && total === 1 && !selectCompanyMutation.isPending) {
      console.log('Auto-selecting single company:', companies[0].id);
      selectCompanyMutation.mutate(companies[0].id);
    }
  }, [companies.length, total, debouncedSearch, selectCompanyMutation.isPending]);

  // Reset page when search or pageSize changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  const handleSelectCompany = useCallback((companyId: string) => {
    selectCompanyMutation.mutate(companyId);
  }, [selectCompanyMutation]);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
  }, []);

  const goToPage = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  }, [totalPages]);

  // No renderizar nada si no es super admin (el useEffect se encarga de redirigir)
  if (!isSuperAdmin) {
    return null;
  }

  if (isLoading && !companiesData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando empresas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <BuildingOfficeIcon className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Error al cargar empresas</h2>
          <p className="mt-2 text-gray-600">
            No se pudieron obtener las empresas. Por favor, intenta nuevamente.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Reintentar
          </button>
          <button
            onClick={() => navigate('/login')}
            className="mt-2 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Volver al Login
          </button>
        </div>
      </div>
    );
  }

  if (total === 0 && !debouncedSearch) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <BuildingOfficeIcon className="h-6 w-6 text-yellow-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Sin empresas asignadas</h2>
          <p className="mt-2 text-gray-600">
            Tu usuario no tiene empresas asignadas. Por favor, contacta al administrador.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Volver al Login
          </button>
        </div>
      </div>
    );
  }

  // If only one company and auto-selecting, show loading
  if (companies.length === 1 && !debouncedSearch && total === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Configurando empresa...</p>
          <p className="mt-2 text-sm text-gray-500">{companies[0].name}</p>
        </div>
      </div>
    );
  }

  // Multiple companies - show selector with search and pagination
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-600 mb-4">
            <BuildingOfficeIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Selecciona una Empresa</h1>
          <p className="mt-2 text-gray-600">
            Tienes acceso a {total} {total === 1 ? 'empresa' : 'empresas'}
          </p>
        </div>

        {/* Search Bar and Page Size Selector */}
        <div className="max-w-2xl mx-auto mb-8 flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Buscar empresa por nombre..."
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm shadow-sm"
            />
            {isFetching && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-3 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
              title="Vista de tarjetas"
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-3 transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              }`}
              title="Vista de lista"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <label htmlFor="pageSize" className="text-sm text-gray-600 whitespace-nowrap">
              Mostrar:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="block py-3 px-4 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm shadow-sm"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Companies Grid with Lateral Navigation */}
        {companies.length > 0 ? (
          <div className="relative flex items-center">
            {/* Left Arrow */}
            {totalPages > 1 && (
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
                className="absolute -left-4 lg:-left-16 z-10 p-3 rounded-full bg-white shadow-lg border border-gray-200 text-gray-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200 transition-all duration-200"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
            )}

            <div className="flex-1 px-8 lg:px-12">
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {companies.map((company) => (
                    <CompanyCard
                      key={company.id}
                      company={company}
                      isSelecting={selectCompanyMutation.isPending}
                      subscriptionInfo={subscriptionStatuses?.[company.id]}
                      onSelect={handleSelectCompany}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">RUC</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Código</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Plan</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {companies.map((company) => {
                        const subInfo = subscriptionStatuses?.[company.id];
                        return (
                          <tr
                            key={company.id}
                            className="hover:bg-primary-50 transition-colors cursor-pointer"
                            onClick={() => !selectCompanyMutation.isPending && handleSelectCompany(company.id)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                {company.image ? (
                                  <img src={company.image} alt={company.name} className="h-8 w-8 rounded-md object-cover" />
                                ) : (
                                  <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                                    <BuildingOfficeIcon className="h-4 w-4 text-white" />
                                  </div>
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{company.name}</div>
                                  {company.email && (
                                    <div className="text-xs text-gray-500">{company.email}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                              <span className="text-sm text-gray-600 font-mono">{company.ruc || '—'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                              <span className="text-sm text-gray-600 font-mono">{company.code || '—'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                              {subInfo ? (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  subInfo.isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {subInfo.planName || 'Sin Plan'}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectCompany(company.id);
                                }}
                                disabled={selectCompanyMutation.isPending}
                                className="text-sm font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Seleccionar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Right Arrow */}
            {totalPages > 1 && (
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page === totalPages}
                className="absolute -right-4 lg:-right-16 z-10 p-3 rounded-full bg-white shadow-lg border border-gray-200 text-gray-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200 transition-all duration-200"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No se encontraron empresas</h3>
            <p className="mt-1 text-sm text-gray-500">
              Intenta con otro término de búsqueda.
            </p>
          </div>
        )}

        {/* Page info */}
        {totalPages > 1 && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Página {page} de {totalPages} ({total} empresas en total)
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            No ves tu empresa?{' '}
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Contacta al administrador
            </button>
          </p>
        </div>
      </div>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsContactModalOpen(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Contactar Soporte
              </h3>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Visita nuestro portal de soporte para obtener ayuda:
              </p>

              {/* Portal de Soporte */}
              <a
                href="https://soporte.facturalibre.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="p-2 bg-primary-100 rounded-lg">
                  <GlobeAltIcon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Portal de Soporte</p>
                  <p className="text-sm text-primary-600">soporte.facturalibre.io</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
